import * as React from 'react'

import { Box } from '@factorialco/deprecated-design-system'
import { isAfter, subDays } from 'date-fns'
import moment from 'moment'

import { i18nDateToString } from 'modules/core/lib/dates'

import { useAttendanceDateNavigator } from 'modules/attendance/lib/attendanceDateNavigatorContext'
import { useTimesheetData } from 'modules/attendance/lib/timesheetDataContext'
import { TimesheetFeatures } from 'modules/attendance/lib/timesheetFeatures'

import BreakdownAccordion from './BreakdownAccordion'
import Overview from './Overview'

interface Props {
  timeSheetData: ReturnType<typeof useTimesheetData>
}

const BankOfHoursBreakdown = ({ timeSheetData }: Props) => {
  const { lastCycle, employeeId, featureByName, openPeriod, cycles, isPartTime } = timeSheetData
  const { dateFrom, dateTo } = useAttendanceDateNavigator()

  const hasBankOfHours = TimesheetFeatures.BANK_OF_HOURS in featureByName
  const hasBankOfHoursV2 = TimesheetFeatures.BANK_OF_HOURS_V2 in featureByName
  const shouldUseBankOfHoursV2Flow = hasBankOfHours && hasBankOfHoursV2

  const normalizeDate = (rawDate: Date) => {
    const normalized = new Date(rawDate)
    normalized.setHours(0, 0, 0, 0)
    return normalized
  }

  const cycleStartTimestamp = lastCycle
    ? moment(lastCycle.startDate, 'YYYY-MM-DD').valueOf()
    : dateFrom.getTime()
  const cycleEndTimestamp = lastCycle
    ? moment(lastCycle.endDate, 'YYYY-MM-DD').valueOf()
    : dateTo.getTime()

  const defaultStartTimestamp = shouldUseBankOfHoursV2Flow
    ? dateFrom.getTime()
    : cycleStartTimestamp
  const defaultEndTimestamp = shouldUseBankOfHoursV2Flow ? dateTo.getTime() : cycleEndTimestamp

  const defaultStartDate = React.useMemo(
    () => normalizeDate(new Date(defaultStartTimestamp)),
    [defaultStartTimestamp]
  )
  const defaultEndDate = React.useMemo(
    () => normalizeDate(new Date(defaultEndTimestamp)),
    [defaultEndTimestamp]
  )

  const [hasCustomRange, setHasCustomRange] = React.useState(false)
  const [selectedRange, setSelectedRange] = React.useState<{
    startDate: Date
    endDate: Date
  }>({
    startDate: defaultStartDate,
    endDate: defaultEndDate,
  })

  React.useEffect(() => {
    if (!shouldUseBankOfHoursV2Flow) {
      setHasCustomRange(false)
    }
  }, [shouldUseBankOfHoursV2Flow])

  React.useEffect(() => {
    if (!hasCustomRange) {
      setSelectedRange({
        startDate: defaultStartDate,
        endDate: defaultEndDate,
      })
    }
  }, [defaultStartDate, defaultEndDate, hasCustomRange])

  const handleDateRangeChange = React.useCallback((startDate: Date, endDate: Date) => {
    setHasCustomRange(true)
    setSelectedRange({
      startDate: normalizeDate(startDate),
      endDate: normalizeDate(endDate),
    })
  }, [])

  const today = normalizeDate(new Date())
  const yesterday = subDays(today, 1)

  const startDate = normalizeDate(selectedRange.startDate)
  const endDate = normalizeDate(selectedRange.endDate)

  const isFutureRange =
    shouldUseBankOfHoursV2Flow &&
    startDate.getTime() > yesterday.getTime() &&
    endDate.getTime() > yesterday.getTime()

  let effectiveStartDate: Date | undefined
  let effectiveEndDate: Date | undefined

  if (!isFutureRange) {
    effectiveEndDate =
      shouldUseBankOfHoursV2Flow &&
      (isAfter(endDate, today) || endDate.getTime() === today.getTime())
        ? yesterday
        : endDate

    effectiveStartDate = startDate
    if (shouldUseBankOfHoursV2Flow && isAfter(effectiveStartDate, effectiveEndDate)) {
      effectiveStartDate = effectiveEndDate
    }
  }

  const startOn = effectiveStartDate ? i18nDateToString(effectiveStartDate, 'backend') : undefined
  const endOn = effectiveEndDate ? i18nDateToString(effectiveEndDate, 'backend') : undefined

  const selectedCycle = shouldUseBankOfHoursV2Flow
    ? cycles.find((cycle) => {
        const cycleStart = moment(cycle.startDate, 'YYYY-MM-DD')
        const cycleEnd = moment(cycle.endDate, 'YYYY-MM-DD')

        return (
          (cycleStart.isSameOrBefore(startDate, 'day') &&
            cycleEnd.isSameOrAfter(startDate, 'day')) ||
          (cycleStart.isSameOrBefore(endDate, 'day') && cycleEnd.isSameOrAfter(endDate, 'day')) ||
          (cycleStart.isSameOrAfter(startDate, 'day') && cycleEnd.isSameOrBefore(endDate, 'day'))
        )
      })
    : lastCycle

  const selectedCycleClosed =
    !!selectedCycle && moment(selectedCycle.endDate, 'YYYY-MM-DD').isBefore(today, 'day')

  const isSelectedRangeClosed =
    !selectedCycle && !isFutureRange && moment(endDate).isBefore(today, 'day')

  const showClosedCycleWarning = shouldUseBankOfHoursV2Flow
    ? Boolean(!isFutureRange && (selectedCycleClosed || isSelectedRangeClosed))
    : !openPeriod

  if (!lastCycle && !shouldUseBankOfHoursV2Flow) return null

  return (
    <Box gap="s16" padding="s24">
      <Overview timeSheetData={timeSheetData} />
      <BreakdownAccordion
        employeeId={employeeId}
        startOn={startOn}
        endOn={endOn}
        enableCustomRange={shouldUseBankOfHoursV2Flow}
        selectedStartDate={startDate}
        selectedEndDate={endDate}
        showClosedCycleWarning={showClosedCycleWarning}
        isPartTime={isPartTime}
        onDateRangeChange={handleDateRangeChange}
      />
    </Box>
  )
}

export default BankOfHoursBreakdown
